import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Center, SimpleGrid } from "@chakra-ui/react";
import { cancelSession } from "api/webservice/sessions";
import { useVideoContext } from "hooks/session";
import { useToast } from "hooks";
import { Callback, Session } from "types";
import { useModal } from "context/modal";
import { isCancellableSession } from "utils/session";
import useGameLiftStreams from "hooks/session/useGameLiftStreams";

import { ReactComponent as NoShowModalImage } from "assets/no-show-modal-image.svg";

interface ModalProps {
  callback: Callback;
  close: Callback;
}

const NoShowModal: React.FC<ModalProps> = ({ callback, close }: ModalProps) => (
  <>
    <Box textAlign="center" mb="1rem">
      <Center>
        <NoShowModalImage width="80%" height="80%"/>
      </Center>
      It seems that you are leaving the session early. Would you like to <b>cancel
      this session</b> and mark it as a &quot;No Show&quot;?
    </Box>
    <SimpleGrid columns={1} gap={6}>
      <Button size="sm" onClick={close}>
        Exit Session
      </Button>
      <Button size="sm" onClick={callback} variant="red">
        Cancel & Mark as &quot;No Show&quot;
      </Button>
    </SimpleGrid>
  </>
);

interface Props {
  session: Session;
}

const EndCallButton: React.FC<Props> = ({ session }: Props) => {
  const { room } = useVideoContext();
  const { gameDisconnect } = useGameLiftStreams(session.id);
  const history = useHistory();
  const toast = useToast();
  const { openModal, closeModal, setTitle, setContent } = useModal()!;
  const sessionId = session!.id;

  // TODO make this also terminate the gameliftstreams session
  const handleEndCall = (): void => {
    closeModal();
    gameDisconnect();
    room!.disconnect();
    history.push("/sessions");
  };

  const handleNoShow = (): void => {
    cancelSession({ ...session, noShow: true })
      .then(() => {
        handleEndCall();
      })
      .catch(err =>
        toast({
          title: "Failed to cancel instance",
          description: err.response.data.response,
          status: "error"
        })
      );
  };

  const handleClick = (): void => {
    if (session && isCancellableSession(session)) {
      setTitle("Is the client a No-Show?");
      setContent(<NoShowModal callback={handleNoShow} close={handleEndCall} />);
      openModal();
    } else {
      //disconnect gameliftstreams and the room before going to the next page
      gameDisconnect();
      room!.disconnect();
      history.push(`/session/${sessionId}/completionpage`);
    }
  };

  return (
    <Button variant="red" size="lg" onClick={handleClick} data-cy-disconnect>
      End Call
    </Button>
  );
};

export default EndCallButton;
